import {
  Injectable,
  Renderer2,
  RendererFactory2,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

export interface PendoVisitorDetails {
  id: string;
  role?: string;
  country?: string;
}

@Injectable({ providedIn: 'root' })
export class PendoService {
  private static readonly PENDO_URL_PATTERN =
    /^https:\/\/cdn\.pendo\.io\/agent\/static\/[0-9a-f-]+\/pendo\.js$/i;
  private static readonly DISABLE_STORAGE_KEY = 'pendo:disabled';

  private readonly renderer: Renderer2;
  private initialized = false;

  constructor(
    rendererFactory: RendererFactory2,
    private readonly sanitizer: DomSanitizer,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  initialize(visitor: PendoVisitorDetails): void {
    if (this.isPendoDisabled()) {
      this.installNoopPendo();
      return;
    }

    if (!environment.pendoScriptUrl || this.initialized) {
      return;
    }

    if (!PendoService.PENDO_URL_PATTERN.test(environment.pendoScriptUrl)) {
      return;
    }

    this.initialized = true;
    this.loadAgent(environment.pendoScriptUrl)
      .then(() => {
        globalThis.pendo?.initialize({
          visitor: {
            ...visitor,
            environment: environment.environment,
          },
          account: {
            id: environment.applicationName,
          },
        });
      })
      .catch(() => this.installNoopPendo());
  }

  track(eventName: string, metadata?: Record<string, unknown>): void {
    if (this.initialized && globalThis.pendo?.isReady?.()) {
      globalThis.pendo.track(eventName, metadata);
    }
  }

  private isPendoDisabled(): boolean {
    try {
      if (localStorage.getItem(PendoService.DISABLE_STORAGE_KEY) === 'true') {
        return true;
      }
    } catch {
      // Ignore unavailable local storage and use the environment switch.
    }

    return !environment.pendoEnabled;
  }

  private installNoopPendo(): void {
    if (globalThis.pendo?.isReady?.()) {
      return;
    }

    const noop = (): void => undefined;
    globalThis.pendo = {
      initialize: noop,
      updateOptions: noop,
      track: noop,
      isReady: () => false,
    };
  }

  private loadAgent(scriptUrl: string): Promise<void> {
    const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, scriptUrl);
    if (!sanitizedUrl) {
      return Promise.reject(new Error('Pendo URL rejected by sanitizer'));
    }

    return new Promise((resolve, reject) => {
      const script = this.renderer.createElement('script');
      this.renderer.setAttribute(script, 'async', 'true');
      this.renderer.setAttribute(script, 'src', sanitizedUrl);
      this.renderer.listen(script, 'load', () => resolve());
      this.renderer.listen(script, 'error', () =>
        reject(new Error('Failed to load Pendo agent')),
      );
      this.renderer.appendChild(document.head, script);
    });
  }
}